import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import { CheckCircle2, AlertCircle, Clock, ShieldAlert, ShieldCheck, Lock, ChevronDown, ChevronUp, Upload } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Iter37 sexies — Real-time profile status card for MNS.
 * Aggregates `/api/profile/mns` (completeness) + `/api/diplomas` + `/api/diplomas/required`
 * to show a clear "ready to publish slots" status with full per-document breakdown.
 *
 * Props:
 *   onUploadDoc?: (doc_type: string, label: string, requires_expiry: boolean) => void
 */
function StatusBadge({ d }) {
  if (!d) return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />Manquant</span>;
  if (d.validation_status === "validated") return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Validé</span>;
  if (d.validation_status === "rejected") return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1" title={d.rejection_reason || ""}><AlertCircle className="w-3 h-3" />Rejeté</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1"><Clock className="w-3 h-3" />En attente admin</span>;
}

export default function ProfileStatusCard({ onUploadDoc }) {
  const [data, setData] = useState(null); // { profile, diplomas, required }
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/profile/mns"), api.get("/diplomas"), api.get("/diplomas/required")])
      .then(([p, dl, rq]) => {
        if (cancelled) return;
        setData({ profile: p.data, diplomas: dl.data || [], required: rq.data || [] });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const { profile, diplomas, required } = data;
    const completeness = profile?.completeness || {};
    const latestByType = (dt) => {
      const arr = diplomas.filter((x) => x.doc_type === dt);
      return arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
    };
    const requiredItems = required.filter((r) => r.required !== false);
    const rows = requiredItems.map((r) => {
      const d = latestByType(r.doc_type);
      let daysLeft = null;
      if (d?.valid_until && r.requires_expiry !== false) {
        const days = Math.floor((new Date(d.valid_until) - new Date()) / 86400000);
        daysLeft = days;
      }
      return { ...r, doc: d, daysLeft };
    });
    // Iter52 fix: also honor completeness.hard_block (the source of truth from
    // the backend cron), not just users.mns_hard_blocked which /api/profile/mns
    // now exposes but may not have been synced yet.
    const isHardBlocked = !!profile?.mns_hard_blocked || !!(completeness?.hard_block);
    // Iter52 fix: any expired doc must break the "profil complet" state — even if it hasn't
    // yet crossed the 30-day hard-block threshold. Previously only `mns_hard_blocked` (set by
    // the daily cron) gated the green banner, so users saw a false "profil complet" between
    // the expiration date and the 30-day grace-period end.
    const hasExpired = (completeness.expired || []).length > 0;
    const isReady = !isHardBlocked && !hasExpired && completeness.complete === true;
    const blockers = [];
    if (isHardBlocked) blockers.push({ kind: "hard_block", text: "Documents expirés depuis plus de 30 jours — publication suspendue." });
    (completeness.missing || []).forEach((m) => {
      if (m === "siret") blockers.push({ kind: "missing_field", text: "SIRET non renseigné dans le profil pro." });
      else if (m === "vat_rate") blockers.push({ kind: "missing_field", text: "Taux de TVA non renseigné (vous êtes assujetti)." });
      else if (m === "identity") blockers.push({ kind: "missing_field", text: "Prénom + nom non renseignés." });
      else if (m === "insurance") blockers.push({ kind: "missing_doc", text: "Attestation d'assurance professionnelle manquante ou non validée." });
      else {
        const lbl = required.find((r) => r.doc_type === m)?.label || m;
        blockers.push({ kind: "missing_doc", doc_type: m, text: `Document manquant ou non validé : ${lbl}` });
      }
    });
    (completeness.expiring_soon || []).forEach((e) => {
      const lbl = required.find((r) => r.doc_type === e.doc_type)?.label || e.doc_type;
      const days = Math.max(0, Math.floor((new Date(e.valid_until) - new Date()) / 86400000));
      blockers.push({ kind: "expiring", text: `« ${lbl} » expire dans ${days} jour${days > 1 ? "s" : ""} — pensez à renouveler.` });
    });
    (completeness.expired || []).forEach((e) => {
      const lbl = required.find((r) => r.doc_type === e.doc_type)?.label || e.doc_type;
      blockers.push({ kind: "expired", text: `« ${lbl} » expiré depuis le ${e.valid_until?.slice(0, 10) || "—"} — renouvelez sous 30 jours.` });
    });
    return { rows, isHardBlocked, isReady, blockers, completeness };
  }, [data]);

  if (!view) return (
    <div className="card-soft p-5 animate-pulse" data-testid="profile-status-loading">
      <div className="h-5 bg-slate-200 rounded w-1/3 mb-3" />
      <div className="h-4 bg-slate-100 rounded w-2/3" />
    </div>
  );

  const { rows, isHardBlocked, isReady, blockers } = view;
  const validatedCount = rows.filter((r) => r.doc?.validation_status === "validated").length;
  const total = rows.length;
  const pct = total ? Math.round((validatedCount / total) * 100) : 0;

  let bannerClass, bannerIcon, bannerTitle, bannerSubtitle;
  if (isHardBlocked) {
    bannerClass = "bg-red-50 border-red-300 text-red-900";
    bannerIcon = <Lock className="w-7 h-7 text-red-600 shrink-0" />;
    bannerTitle = "Publication de créneaux suspendue";
    bannerSubtitle = "Un ou plusieurs documents obligatoires sont expirés depuis plus de 30 jours.";
  } else if (isReady) {
    bannerClass = "bg-emerald-50 border-emerald-300 text-emerald-900";
    bannerIcon = <ShieldCheck className="w-7 h-7 text-emerald-600 shrink-0" />;
    bannerTitle = "Profil complet — vous pouvez publier vos créneaux";
    bannerSubtitle = `${validatedCount} / ${total} documents obligatoires validés.`;
  } else {
    bannerClass = "bg-amber-50 border-amber-300 text-amber-900";
    bannerIcon = <ShieldAlert className="w-7 h-7 text-amber-600 shrink-0" />;
    bannerTitle = "Profil incomplet — publication impossible";
    bannerSubtitle = `${validatedCount} / ${total} documents obligatoires validés. ${blockers.length} blocage${blockers.length > 1 ? "s" : ""} à résoudre.`;
  }

  return (
    <div className={`card-soft p-5 border-2 ${bannerClass}`} data-testid="profile-status-card">
      <div className="flex items-start gap-3 flex-wrap">
        {bannerIcon}
        <div className="flex-1 min-w-[260px]">
          <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>{bannerTitle}</h3>
          <p className="text-sm mt-1 opacity-90">{bannerSubtitle}</p>
          <div className="mt-3 w-full bg-white/60 rounded-full overflow-hidden h-2.5">
            <div
              className={`h-full transition-all ${isHardBlocked ? "bg-red-500" : isReady ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${pct}%` }}
              data-testid="profile-status-progress"
            />
          </div>
          <div className="text-xs mt-1 opacity-75">{pct}% validés ({validatedCount}/{total})</div>
        </div>
        {blockers.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full bg-white/80"
            onClick={() => setShowWhy((v) => !v)}
            data-testid="profile-status-why-btn"
          >
            {showWhy ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            Pourquoi ? ({blockers.length})
          </Button>
        )}
      </div>

      {showWhy && blockers.length > 0 && (
        <div className="mt-4 bg-white/70 rounded-lg p-3 space-y-1.5" data-testid="profile-status-why-list">
          <h4 className="font-semibold text-sm mb-2">Détail des blocages :</h4>
          <ul className="text-sm space-y-1">
            {blockers.map((b, idx) => (
              <li key={`${b.kind}-${b.doc_type || idx}`} className="flex items-start gap-2" data-testid={`blocker-${b.kind}`}>
                <span className="mt-0.5">
                  {b.kind === "hard_block" ? <Lock className="w-3.5 h-3.5 text-red-600" />
                    : b.kind === "expired" ? <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                    : b.kind === "expiring" ? <Clock className="w-3.5 h-3.5 text-amber-600" />
                    : <AlertCircle className="w-3.5 h-3.5 text-amber-600" />}
                </span>
                <span className="flex-1">{b.text}</span>
                {b.doc_type && onUploadDoc && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full h-6 text-xs"
                    onClick={() => {
                      const r = rows.find((x) => x.doc_type === b.doc_type);
                      if (r) onUploadDoc(b.doc_type, r.label, r.requires_expiry !== false);
                    }}
                    data-testid={`blocker-action-${b.doc_type}`}
                  >
                    <Upload className="w-3 h-3 mr-0.5" />Téléverser
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Compact per-doc breakdown */}
      <div className="mt-4 grid sm:grid-cols-2 gap-1.5">
        {rows.map((r) => (
          <div key={r.doc_type} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-white/60 text-sm" data-testid={`profile-status-doc-${r.doc_type}`}>
            <span className="truncate flex-1 font-medium">{r.label}</span>
            {r.daysLeft !== null && r.doc?.validation_status === "validated" && r.daysLeft <= 30 && r.daysLeft > 0 && (
              <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 rounded-full">J-{r.daysLeft}</span>
            )}
            <StatusBadge d={r.doc} />
          </div>
        ))}
      </div>
    </div>
  );
}
